import { ArrowUpRight, TriangleAlert, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useApi } from "@/lib/api";

const POLL_INTERVAL_MS = 60_000;

const DISMISSED_KEY = "tendra.dismissed_disruptions";

export interface ServiceDisruption {
  id: string;
  name: string;
  url: string | null;
  message: string | null;
  updatedAt: string | null;
}

interface ServiceStatus {
  pageUrl: string | null;
  disruptions: ServiceDisruption[];
}

export function dismissKey(disruption: ServiceDisruption) {
  return `${disruption.id}:${disruption.updatedAt ?? ""}`;
}

export function visibleDisruptions(
  disruptions: ServiceDisruption[],
  dismissed: string[],
) {
  return disruptions.filter((d) => !dismissed.includes(dismissKey(d)));
}

function readDismissed(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}

function writeDismissed(keys: string[]) {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(keys));
  } catch {
    // Storage may be unavailable (for example, in private browsing), in which case
    // the banner simply reappears on reload.
  }
}

export function ServiceStatusBanner() {
  const { get } = useApi();
  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [dismissed, setDismissed] = useState(readDismissed);

  const load = useCallback(() => {
    void get<ServiceStatus>("/service-status").then((data) => {
      if (data) {
        setStatus(data);
      }
    });
  }, [get]);

  useEffect(load, [load]);

  useEffect(() => {
    const interval = setInterval(load, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [load]);

  const visible = visibleDisruptions(status?.disruptions ?? [], dismissed);
  const current = visible[0];

  if (!status || !current) {
    return null;
  }

  const href =
    visible.length > 1 ? status.pageUrl : (current.url ?? status.pageUrl);

  function dismiss() {
    const keys = [
      ...dismissed.filter((key) =>
        status!.disruptions.some((d) => dismissKey(d) === key),
      ),
      ...visible.map(dismissKey),
    ];

    writeDismissed(keys);
    setDismissed(keys);
  }

  return (
    <div
      role="status"
      data-service-status-banner
      className="flex shrink-0 items-start gap-2 bg-warning py-2 pl-4 pr-2 text-sm text-background md:items-center"
    >
      <TriangleAlert
        className="mt-0.5 h-4 w-4 shrink-0 md:mt-0"
        aria-hidden="true"
      />

      <p className="line-clamp-3 min-w-0 flex-1 md:line-clamp-2">
        <strong className="font-semibold">{current.name}.</strong>{" "}
        {current.message}
        {visible.length > 1 && ` (+${visible.length - 1} more)`}
      </p>

      {href && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-0.5 self-center font-semibold underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-background"
        >
          View status
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      )}

      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="flex h-7 w-7 shrink-0 items-center justify-center self-center rounded-md hover:bg-background/15 focus-visible:outline-2 focus-visible:outline-background"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
